import { LocalizationProvider, MobileTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Fragment, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { minuteMs } from "verdi-common/timeConstants";
import { Icon } from "@/components/icon/icon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Toggle, ToggleGroup, ToggleGroupSeparator } from "@/components/ui/toggle-group";
import { NAMESPACES } from "@/i18n/constants";
import { ICON_KEYS } from "@/icons";
import { cn } from "@/utils/class-name";
import { TOGGLE_GROUP_ACTIVE_CLASS } from "./form-constants";
import { FormValidationMessage } from "./form-validation-message";
import type { PresetTimeUnitKey, StartTimePreset } from "./irrigation-presets";
import { getWallTimeFromClockDate, isSameClockMinute } from "./start-time-clock-utils";
import { resolveStartMs, validateStartDelay, type StartToggleValue } from "./start-time-validation";

function getToggleGroupValue(customEntered: boolean, toggle: StartToggleValue | null): string[] {
    if (customEntered) return [];
    if (toggle === null) return [];
    if (toggle === "now") return ["now"];
    return [String(toggle)];
}

interface StartTimePickerProps {
    onStartTimeChange: (startTime: Date | null, error: string | null) => void;
    presets: StartTimePreset[];
}

export function StartTimePicker({ onStartTimeChange, presets }: StartTimePickerProps) {
    const [nowMs, setNowMs] = useState(() => Date.now());
    const [toggle, setToggle] = useState<StartToggleValue | null>("now");
    const [presetSnapMs, setPresetSnapMs] = useState(() => Date.now());
    const [customInput, setCustomInput] = useState("");
    const [clockStartMs, setClockStartMs] = useState<number | null>(null);
    const [clockOpen, setClockOpen] = useState(false);
    // Placeholder — always overwritten by handleOpenClock before the picker opens
    const [clockValue, setClockValue] = useState(() => new Date());

    const { t } = useTranslation(NAMESPACES.FEATURES.CARDS, {
        keyPrefix: "zone.on-demand-irrigation.create-event-form",
    });
    const { t: tOnDemandValidationErrors } = useTranslation(NAMESPACES.FEATURES.CARDS, {
        keyPrefix: "zone.on-demand-irrigation.shared-validation-errors",
    });

    const customEntered = customInput !== "" || clockStartMs !== null;
    const resolvedStartMs = resolveStartMs({ nowMs, customInput, clockStartMs, toggle, presetSnapMs });
    const validationError = validateStartDelay(tOnDemandValidationErrors, nowMs, resolvedStartMs, customInput);
    const resolvedDisplayStartMs = resolvedStartMs ?? nowMs;

    useEffect(() => {
        const id = setInterval(() => setNowMs(Date.now()), minuteMs);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        if (validationError !== null) {
            onStartTimeChange(null, validationError);
            return;
        }

        if (resolvedStartMs === null) {
            onStartTimeChange(null, null);
            return;
        }

        if (clockStartMs !== null && resolvedStartMs < nowMs) {
            setClockStartMs(null);
            setToggle("now");
            onStartTimeChange(null, null);
            return;
        }

        if (!customEntered && toggle !== "now" && toggle !== null && resolvedStartMs < nowMs) {
            setToggle("now");
            setPresetSnapMs(Date.now());
            onStartTimeChange(null, null);
            return;
        }

        onStartTimeChange(new Date(resolvedStartMs), null);
    }, [clockStartMs, customEntered, nowMs, onStartTimeChange, resolvedStartMs, toggle, validationError]);

    const handleClickNow = () => {
        setToggle("now");
        setCustomInput("");
        setClockStartMs(null);
    };

    const handleClickPreset = (offsetMs: number) => {
        // Sync nowMs so endMs is fresh — prevents immediate expiry for long presets (e.g. 6h)
        const now = Date.now();
        setNowMs(now);
        setToggle(offsetMs);
        setPresetSnapMs(now);
        setCustomInput("");
        setClockStartMs(null);
    };

    const presetToggleLabel = (amount: number, unit: PresetTimeUnitKey) =>
        unit === "hours"
            ? t("start-time.preset-label-hours", { amount })
            : t("start-time.preset-label-minutes", { amount });

    const handleOpenClock = () => {
        setClockValue(new Date(resolvedDisplayStartMs));
        setClockOpen(true);
    };

    const handleClockChange = (newDate: Date | null) => {
        if (newDate === null) return;
        setClockValue(newDate);
    };

    const handleClockAccept = (newDate: Date | null) => {
        if (newDate === null) return;

        // Sync nowMs so resolution and max-delay validation use a fresh instant
        const now = Date.now();
        setNowMs(now);

        if (isSameClockMinute(now, newDate)) {
            handleClickNow();
            return;
        }

        const target = getWallTimeFromClockDate(now, newDate);
        if (target === null) return;

        setClockStartMs(target.getTime());
        setCustomInput("");
        setToggle(null);
    };

    return (
        <div className={"flex flex-col gap-1"}>
            <p id={"start-time-label"} className={"text-sm"}>
                {t("start-time.title")}
            </p>
            <div className={"flex flex-col gap-2"}>
                <ToggleGroup
                    aria-labelledby={"start-time-label"}
                    variant={"outline"}
                    size={"lg"}
                    value={getToggleGroupValue(customEntered, toggle)}
                    className={TOGGLE_GROUP_ACTIVE_CLASS}
                >
                    <Toggle className={"text-sm xs:text-base"} value={"now"} onClick={handleClickNow}>
                        {t("start-time.earliest-start-button")}
                    </Toggle>
                    {presets.map(({ amount, offsetMs, unit }) => (
                        <Fragment key={offsetMs}>
                            <ToggleGroupSeparator />
                            <Toggle
                                className={"text-sm xs:text-base"}
                                value={String(offsetMs)}
                                onClick={() => handleClickPreset(offsetMs)}
                            >
                                {presetToggleLabel(amount, unit)}
                            </Toggle>
                        </Fragment>
                    ))}
                </ToggleGroup>
                <div className={"flex flex-row gap-2 items-center"}>
                    <Input
                        size={"lg"}
                        className={cn("w-full", customEntered && "border-primary")}
                        aria-label={t("start-time.placeholder")}
                        placeholder={t("start-time.placeholder")}
                        type={"number"}
                        value={customInput}
                        onChange={(e) => {
                            setCustomInput(e.target.value);
                            setClockStartMs(null);
                            setToggle(e.target.value === "" ? "now" : null);
                        }}
                    />
                    <Button
                        className={cn("p-4.5 bg-background")}
                        variant={"ghost"}
                        size={"icon-sm"}
                        aria-label={t("start-time.clock-label")}
                        onClick={handleOpenClock}
                    >
                        <Icon icon={ICON_KEYS.SCHEDULE} iconClassName={"[&_path]:fill-foreground"} />
                    </Button>
                </div>

                {/* MobileTimePicker uses a dialog by default */}
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <MobileTimePicker
                        open={clockOpen}
                        onClose={() => setClockOpen(false)}
                        onAccept={handleClockAccept}
                        value={clockValue}
                        onChange={handleClockChange}
                        // is opened by another component, so omit the input
                        renderInput={() => <></>}
                    />
                </LocalizationProvider>
            </div>
            {validationError !== null && <FormValidationMessage errorMessage={validationError} />}
        </div>
    );
}
